/**
 * Thin HTTP client for mcp.clawnify.com. Talks JSON-RPC 2.0 over POST
 * per the MCP Streamable HTTP transport. Reused by the `execute`,
 * `docs`, and `sdk inspect` subcommands; the stdio bridge uses raw
 * pass-through instead.
 */

import { getValidToken, loadAuth } from "./auth.js";

const MCP_URL = "https://mcp.clawnify.com/mcp";

export interface McpToolResult {
  content: Array<
    | { type: "text"; text: string }
    | { type: "image"; data: string; mimeType: string }
    | { type: "resource"; resource: { uri: string; mimeType?: string; text?: string } }
  >;
  isError?: boolean;
  structuredContent?: unknown;
}

export interface McpErrorResponse {
  error: { code: number; message: string };
}

export type McpToolCallResponse = { result: McpToolResult } | McpErrorResponse;

export async function callTool(
  name: string,
  args: Record<string, unknown>,
): Promise<McpToolCallResponse> {
  const token = await getValidToken();
  if (!token) {
    process.stderr.write("Not logged in. Run `clawnify login` first.\n");
    process.exit(1);
  }

  const auth = loadAuth();
  const headers: Record<string, string> = {
    "content-type": "application/json",
    authorization: "Bearer " + token,
  };
  if (auth?.org_id) {
    headers["X-Clawnify-Org-Id"] = auth.org_id;
  }

  const res = await fetch(MCP_URL, {
    method: "POST",
    headers,
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: Date.now(),
      method: "tools/call",
      params: { name, arguments: args },
    }),
  });

  if (res.status === 401) {
    process.stderr.write("Authentication failed. Run `clawnify login` again.\n");
    process.exit(1);
  }

  const body = (await res.json()) as { result?: McpToolResult; error?: { code: number; message: string } };
  if (body.error) return { error: body.error };
  if (!body.result) {
    return { error: { code: -32603, message: "Empty response from MCP gateway" } };
  }
  return { result: body.result };
}

export function isError(resp: McpToolCallResponse): resp is McpErrorResponse {
  return "error" in resp;
}
